import type { TFunction } from 'i18next';
import { i18n } from '../i18n';
import type { ApiError, FieldError } from './types';

// Translate an individual error code (top-level or per-field) using the `errors`
// namespace. Codes are dotted (e.g. "validation.required") and match the JSON
// structure under locales/{lng}/errors.json one-to-one.
//
// Fallback chain: i18n key → FieldError.message → translated `common.internal`.
function translateError(
  t: TFunction,
  err: { code: string; message?: string; params?: Record<string, unknown> | null },
): string {
  const key = `errors:${err.code}`;
  const translated = t(key, {
    defaultValue: err.message || '',
    ...(err.params ?? {}),
  });

  if (translated && translated !== key) return translated;
  if (err.message) return err.message;
  return t('errors:common.internal');
}

// React hook variant — preferred inside components.
export function useErrorTranslator() {
  // Use the global i18n.t so we don't bind to a single namespace.
  const t = i18n.t.bind(i18n) as TFunction;
  return (err: Pick<FieldError, 'code' | 'message' | 'params'>) => translateError(t, err);
}

// Plain-function variant for use outside React (Zustand stores, utilities).
export function translateApiErrorCode(
  err: Pick<FieldError, 'code' | 'message' | 'params'>,
): string {
  const t = i18n.t.bind(i18n) as TFunction;
  return translateError(t, err);
}

// Translate the top-level message of an ApiError. Used by toasts/banners when
// there are no field errors to surface.
export function translateApiError(apiError: ApiError): string {
  return translateApiErrorCode({
    code: apiError.code,
    message: apiError.detail ?? apiError.title,
  });
}
